import React from 'react';
import { Star, Users, ThumbsUp, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { feedbacks } from '../utils/cart';
import ScrollReveal from '../components/ScrollReveal';
import { useGeo } from '../contexts/GeoContext';
import { useTranslation } from '../utils/translations';

const Feedbacks = () => {
  const { isBrazil } = useGeo();
  const tr = useTranslation(isBrazil);
  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 gaming-title glow-red">
              {tr('customerFeedbacks')}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {tr('seeWhatCustomers')}
            </p>
          </div>
        </ScrollReveal>

        {/* Stats Section */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {[
            { icon: Users, label: tr('satisfiedCustomers'), value: '500+', color: 'from-red-600 to-orange-600' },
            { icon: Star, label: tr('averageRating'), value: '5.0', color: 'from-orange-600 to-red-600' },
            { icon: ThumbsUp, label: tr('recommendations'), value: '98%', color: 'from-red-600 to-pink-600' },
            { icon: Award, label: tr('support247'), value: '100%', color: 'from-pink-600 to-red-600' }
          ].map((stat, idx) => (
            <ScrollReveal key={idx} direction="up" delay={idx * 100} scale>
              <Card className="bg-gray-900 border-2 border-red-900/30 hover:border-red-600/50 transition-all">
                <CardContent className="p-6 text-center">
                  <div className={`bg-gradient-to-br ${stat.color} w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                    <stat.icon className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-red-600 gaming-title mb-2">{stat.value}</p>
                  <p className="text-sm text-gray-400 font-medium">{stat.label}</p>
                </CardContent>
              </Card>
            </ScrollReveal>
          ))}
        </div>

        {/* Featured Feedback Images */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 gaming-title flex items-center">
            <Star className="w-6 h-6 mr-2 text-red-600" />
            {tr('realProof')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {feedbacks
              .filter(f => f.image)
              .map((feedback, fIdx) => (
                <ScrollReveal key={feedback.id} direction="up" delay={fIdx * 100}>
                  <Card className="overflow-hidden bg-gray-900 border-2 border-red-900/30 hover:border-red-600/50 transition-all card-premium group">
                    <CardContent className="p-0">
                      <div className="relative overflow-hidden">
                        <img
                          src={feedback.image}
                          alt={`Feedback de ${feedback.username}`}
                          className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <p className="text-white font-bold gaming-title">{feedback.username}</p>
                          <p className="text-gray-300 text-sm">{feedback.date}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              ))}
          </div>
        </div>

        {/* All Feedbacks */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 gaming-title flex items-center">
            <ThumbsUp className="w-6 h-6 mr-2 text-red-600" />
            {tr('testimonials')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {feedbacks.map((feedback) => (
              <Card key={feedback.id} className="bg-gray-900 border-2 border-red-900/30 hover:border-red-600/50 transition-all card-premium">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg text-white gaming-title">{feedback.username}</h3>
                      <p className="text-sm text-gray-500">{feedback.date}</p>
                    </div>
                    <div className="flex">
                      {[...Array(feedback.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-red-600 text-red-600" />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed bg-gray-800 p-4 rounded-lg border border-red-900/20">
                    {feedback.message}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-red-900/20 via-red-800/20 to-red-900/20 rounded-2xl p-12 border-2 border-red-900/30">
          <h3 className="text-2xl md:text-4xl font-bold text-white mb-4 gaming-title">
            {tr('beTheNext')}
          </h3>
          <p className="text-gray-400 text-lg mb-6">
            {tr('joinSatisfied')}
          </p>
          <a href="/produtos">
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-6 text-lg rounded-lg border-2 border-red-500 shadow-lg hover:shadow-red-600/50 transition-all gaming-title">
              {tr('viewProducts')}
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Feedbacks;